import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle2, ChevronDown, Globe2, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SiteLayout } from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSite } from "@/hooks/use-site";
import { contactFormSchema, inquiryTypes, isInquiryType } from "@/lib/contact-inquiry";
import { localizeUrl } from "@/lib/site";
import { cn } from "@/lib/utils";

type FormValues = z.input<typeof contactFormSchema>;

export function ContactPage() {
  const { site, messages, href } = useSite();
  const queryInquiry = new URL(href, site.origin).searchParams.get("inquiry");
  const prefilledInquiry = isInquiryType(queryInquiry) ? queryInquiry : undefined;
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const form = useForm<FormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      country: "",
      phone: "",
      inquiryType: prefilledInquiry,
      message: "",
      consent: false,
      source: prefilledInquiry ? `${prefilledInquiry}-cta` : "contact-page",
      website: "",
    },
  });
  const selectedInquiry = form.watch("inquiryType");
  const privacy = localizeUrl("/privacy", site.locale, href);
  const brandResources = localizeUrl("/brand", site.locale, href);
  const distributors = localizeUrl("/distributors", site.locale, href);
  const endpoint = localizeUrl("/api/contact", site.locale, href);

  useEffect(() => {
    if (prefilledInquiry) {
      form.setValue("inquiryType", prefilledInquiry);
    } else {
      form.resetField("inquiryType", { defaultValue: undefined });
    }
    form.setValue("source", prefilledInquiry ? `${prefilledInquiry}-cta` : "contact-page");
  }, [form, prefilledInquiry]);

  const submit = form.handleSubmit(async (values) => {
    setStatus("idle");
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(values),
      });
      setStatus(response.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  });

  return (
    <SiteLayout>
      <PageBreadcrumb items={[{ label: messages.navContact }]} />
      <section className="bg-background px-4 py-16 sm:px-6 sm:py-24">
        <div className="mx-auto grid max-w-6xl grid-cols-1 overflow-hidden rounded-3xl border border-border bg-card shadow-2xl shadow-foreground/5 animate-in fade-in slide-in-from-bottom-2 duration-500 lg:grid-cols-12">
          {/* LEFT PANEL */}
          <aside className="relative overflow-hidden border-b border-border bg-muted/40 p-8 sm:p-10 md:p-12 lg:col-span-5 lg:border-b-0 lg:border-r">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-32 -top-32 h-64 w-64 rounded-full bg-primary/10 blur-3xl"
            />
            <div className="relative z-10 flex h-full flex-col">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                {messages.contactsEyebrow}
              </p>
              <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl">
                {messages.contactsHeading}
              </h1>
              <p className="mt-6 max-w-sm text-lg text-muted-foreground">
                {messages.contactsIntro}
              </p>

              <div className="mt-10 space-y-4">
                <ContactRow
                  icon={<Mail className="h-5 w-5" />}
                  label="sales@inext.ltd"
                  href="mailto:sales@inext.ltd"
                />
                <ContactRow
                  icon={<Globe2 className="h-5 w-5" />}
                  label={messages.contactRoutingLabel}
                />
              </div>

              <a
                href={distributors}
                className="group mt-12 block rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:border-primary/30 hover:shadow-md"
              >
                <h2 className="font-semibold text-foreground">{messages.findDistributorTitle}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{messages.findDistributorBody}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:underline">
                  {messages.viewDistributors}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            </div>
          </aside>

          {/* RIGHT PANEL */}
          <div className="scroll-mt-24 p-6 sm:p-8 md:p-10 lg:col-span-7 lg:p-12" id="inquiry-form">
            {status === "success" ? (
              <div className="rounded-2xl border border-primary/25 bg-primary/5 p-6">
                <CheckCircle2 className="h-7 w-7 text-primary" />
                <h3 className="mt-3 text-lg font-semibold text-foreground">
                  {messages.contactSuccessTitle}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{messages.contactSuccessBody}</p>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-foreground">
                    {messages.contactFormTitle}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">{messages.contactFormIntro}</p>
                </div>

                <form className="space-y-6" onSubmit={submit}>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <Field
                      label={messages.contactNameLabel}
                      error={form.formState.errors.name?.message}
                    >
                      <Input autoComplete="name" {...form.register("name")} />
                    </Field>
                    <Field
                      label={messages.contactEmailLabel}
                      error={form.formState.errors.email?.message}
                    >
                      <Input autoComplete="email" type="email" {...form.register("email")} />
                    </Field>
                    <Field
                      label={
                        selectedInquiry === "support"
                          ? messages.contactCompanyOptionalLabel
                          : messages.contactCompanyLabel
                      }
                      error={
                        form.formState.errors.company?.type === "custom"
                          ? messages.contactCompanyRequired
                          : form.formState.errors.company?.message
                      }
                    >
                      <Input autoComplete="organization" {...form.register("company")} />
                    </Field>
                    <Field
                      label={messages.contactCountryLabel}
                      error={form.formState.errors.country?.message}
                    >
                      <Input autoComplete="country-name" {...form.register("country")} />
                    </Field>
                    <Field label={messages.contactPhoneOptionalLabel}>
                      <Input autoComplete="tel" type="tel" {...form.register("phone")} />
                    </Field>
                    <Field
                      label={messages.contactInquiryTypeLabel}
                      error={form.formState.errors.inquiryType?.message}
                    >
                      <div className="relative">
                        <select
                          className="flex h-10 w-full appearance-none rounded-md border border-input bg-transparent px-3 py-1 pr-10 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={selectedInquiry ?? ""}
                          {...form.register("inquiryType")}
                        >
                          <option value="">{messages.contactInquiryTypePlaceholder}</option>
                          {inquiryTypes.map((type) => (
                            <option key={type} value={type}>
                              {messages[`contactInquiryType_${type}`]}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          aria-hidden
                          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                        />
                      </div>
                    </Field>
                  </div>

                  {selectedInquiry === "brand" && (
                    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm">
                      <p className="text-muted-foreground">{messages.contactBrandResourcesNote}</p>
                      <a
                        href={brandResources}
                        className="group mt-2 inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
                      >
                        {messages.contactBrandResourcesCta}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </a>
                    </div>
                  )}

                  <Field
                    label={messages.contactMessageLabel}
                    error={form.formState.errors.message?.message}
                  >
                    <Textarea className="min-h-32 resize-none" {...form.register("message")} />
                  </Field>

                  <div
                    aria-hidden="true"
                    className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden"
                  >
                    <label htmlFor="website">{messages.contactWebsiteHoneypotLabel}</label>
                    <input
                      id="website"
                      autoComplete="off"
                      tabIndex={-1}
                      {...form.register("website")}
                    />
                  </div>
                  <input type="hidden" {...form.register("source")} />

                  <div>
                    <label className="flex items-start gap-3 text-sm text-muted-foreground">
                      <input className="mt-1" type="checkbox" {...form.register("consent")} />
                      <span>
                        {messages.contactConsentPrefix}{" "}
                        <a className="font-medium text-primary hover:underline" href={privacy}>
                          {messages.contactPrivacyLink}
                        </a>
                        .
                      </span>
                    </label>
                    {form.formState.errors.consent && (
                      <p className="mt-2 text-xs font-medium text-destructive">
                        {messages.contactConsentRequired}
                      </p>
                    )}
                  </div>

                  {status === "error" && (
                    <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
                      <p className="font-medium text-foreground">{messages.contactFailureTitle}</p>
                      <p className="mt-1 text-muted-foreground">
                        {messages.contactFailureBody}{" "}
                        <a
                          className="font-medium text-primary hover:underline"
                          href="mailto:sales@inext.ltd"
                        >
                          sales@inext.ltd
                        </a>
                      </p>
                    </div>
                  )}

                  <Button
                    disabled={form.formState.isSubmitting}
                    type="submit"
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    {form.formState.isSubmitting
                      ? messages.contactSubmitting
                      : messages.contactSubmit}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function ContactRow({
  icon,
  label,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string;
}) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      className={cn(
        "flex items-start gap-4 text-sm text-foreground",
        href && "transition-colors hover:text-primary",
      )}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-card text-primary">
        {icon}
      </span>
      <span className="min-w-0 pt-2 leading-relaxed">{label}</span>
    </Wrapper>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-2">
      <span className="block text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
      {error && <span className="block text-xs font-medium text-destructive">{error}</span>}
    </label>
  );
}
